"use client"

import { useState, useCallback } from "react"
import { ApiError } from "@/lib/api"
import { logger } from "@/lib/logger"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"

type DuplicateInfo = { atsPlacementId: number; workerName: string; clientOrgName: string }

export interface UseAtsCreateModalResult {
  isOpen: boolean
  atsPlacementId: string
  onAtsPlacementIdChange: (value: string) => void
  error: string | null
  duplicate: DuplicateInfo | null
  onOpen: () => void
  onClose: () => void
  onConfirm: () => Promise<void>
  onForce: () => Promise<void>
}

export function useAtsCreateModal(
  atsSource: string | null,
  createAsync: (atsSource: string, atsPlacementId: number, force?: boolean) => Promise<void>,
): UseAtsCreateModalResult {
  const [isOpen, setIsOpen] = useState(false)
  const [atsPlacementId, setAtsPlacementId] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null)

  const { toast } = useToast()
  const { t } = useTranslation()

  const parsedId = atsPlacementId.trim() === "" ? null : Number(atsPlacementId)
  const isValidId = parsedId !== null && Number.isInteger(parsedId) && parsedId > 0

  const onAtsPlacementIdChange = useCallback((value: string) => {
    setError(null)
    setAtsPlacementId(value)
  }, [])

  const onOpen = useCallback(() => {
    setError(null)
    setDuplicate(null)
    setIsOpen(true)
  }, [])

  const onClose = useCallback(() => {
    setIsOpen(false)
    setAtsPlacementId("")
    setError(null)
    setDuplicate(null)
  }, [])

  const onConfirm = useCallback(async () => {
    if (!atsSource || !isValidId || parsedId === null) return
    setError(null)
    try {
      await createAsync(atsSource, parsedId)
      setAtsPlacementId("")
      setIsOpen(false)
      toast({ title: t('atsCreateSuccess', { id: parsedId }) })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError(t('atsCreateNotFound', { id: parsedId }))
          return
        }
        if (err.status === 409) {
          const body = err.body as { workerName?: string; clientOrgName?: string } | null
          setDuplicate({
            atsPlacementId: parsedId,
            workerName: body?.workerName ?? "",
            clientOrgName: body?.clientOrgName ?? "",
          })
          return
        }
      }
      logger.error(err)
      setError(t('atsCreateError'))
    }
  }, [atsSource, isValidId, parsedId, createAsync, toast, t])

  const onForce = useCallback(async () => {
    if (!atsSource || !duplicate) return
    setDuplicate(null)
    try {
      await createAsync(atsSource, duplicate.atsPlacementId, true)
      setAtsPlacementId("")
      setIsOpen(false)
      toast({ title: t('atsCreateSuccess', { id: duplicate.atsPlacementId }) })
    } catch (err) {
      logger.error(err)
      setError(t('atsCreateError'))
    }
  }, [atsSource, duplicate, createAsync, toast, t])

  return { isOpen, atsPlacementId, onAtsPlacementIdChange, error, duplicate, onOpen, onClose, onConfirm, onForce }
}
